import { FormEvent, useState } from 'react';
import {
  useAuditLogs,
  useCreateTenantUser,
  useCurrentUser,
  useSecurityEvents,
  useTenantUsers,
} from '../lib/hooks';
import { loadAuthState } from '../lib/auth-store';
import { getActiveMembership } from '../lib/role-access';
import {
  Button,
  EmptyState,
  Field,
  Icon,
  InlineAlert,
  PageHeader,
  SkeletonList,
  StatusBadge,
  useToast,
} from '../components/ui';

const ROLE_DISPLAY: Record<string, string> = {
  SUPER_ADMIN: 'Platform admin',
  ADMIN: 'Administrator',
  AGENCY: 'Agency owner',
  AGENCY_DEMO: 'Agency (trial)',
  LOCATION_USER: 'Location member',
};

export function UsersPage() {
  const auth = loadAuthState();
  const currentUser = useCurrentUser();
  const usersQuery = useTenantUsers();
  const createUser = useCreateTenantUser();
  const auditLogs = useAuditLogs(1, 50);
  const securityEvents = useSecurityEvents(1, 50);
  const { toast } = useToast();

  const [error, setError] = useState<string | null>(null);

  const activeMembership = getActiveMembership(currentUser.data || auth.user, auth.activeTenantId);
  const activeRole = activeMembership?.role || null;

  if (activeRole === 'LOCATION_USER') {
    return (
      <section className="card">
        <h2>Team</h2>
        <p className="card-sub">
          Team management is available to agency administrators. Ask your agency contact if you need access.
        </p>
      </section>
    );
  }

  async function onCreateUser(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setError(null);

    try {
      await createUser.mutateAsync({
        email: String(form.get('email') || ''),
        name: String(form.get('name') || ''),
        password: String(form.get('password') || ''),
        role: String(form.get('role') || 'LOCATION_USER') as any,
      });
      formElement.reset();
      toast({
        title: 'Team member added',
        description: 'Share the temporary password with them privately — they can change it after signing in.',
      });
    } catch (err) {
      setError((err as Error).message);
    }
  }

  const memberships = (usersQuery.data || []) as any[];

  return (
    <>
      <PageHeader
        title="Team"
        description="Invite people to this workspace. Location members only see the locations you assign to them — ideal for giving clients a window into their own business."
      />

      <div className="grid" style={{ gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 1fr)' }}>
        <section className="card">
          <h3>Add a team member</h3>
          <p className="card-sub">
            They sign in with the email and temporary password you set here.
          </p>
          <form className="grid" onSubmit={onCreateUser}>
            <div className="form-grid">
              <Field label="Full name">
                <input name="name" placeholder="Jordan Smith" required />
              </Field>
              <Field label="Email">
                <input name="email" type="email" placeholder="jordan@client.com" required />
              </Field>
              <Field label="Temporary password" hint="At least 12 characters. They should change it after first sign-in.">
                <input name="password" type="password" placeholder="Minimum 12 characters" required />
              </Field>
              <Field label="Role" hint="Location members can only see locations you assign to them.">
                <select name="role" defaultValue="LOCATION_USER">
                  <option value="LOCATION_USER">Location member</option>
                </select>
              </Field>
            </div>

            {error && <InlineAlert variant="danger">{error}</InlineAlert>}

            <div>
              <Button type="submit" loading={createUser.isPending} loadingText="Adding…">
                Add team member
              </Button>
            </div>
          </form>

          <h3 style={{ marginTop: 22 }}>Current members</h3>
          <div style={{ marginTop: 10 }}>
            {usersQuery.isLoading ? (
              <SkeletonList rows={3} height={40} />
            ) : memberships.length === 0 ? (
              <EmptyState
                icon={<Icon.Users size={20} />}
                title="Just you so far"
                description="Add teammates or client logins with the form above."
              />
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {memberships.map((membership: any) => (
                      <tr key={membership.id}>
                        <td style={{ fontWeight: 600 }}>{membership.user?.name}</td>
                        <td>{membership.user?.email}</td>
                        <td>{ROLE_DISPLAY[membership.role] || membership.role}</td>
                        <td>
                          <StatusBadge status={membership.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>

        <section className="grid">
          <div className="card">
            <h3>Recent account activity</h3>
            <p className="card-sub">A record of changes made in this workspace, newest first.</p>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Action</th>
                    <th>By</th>
                  </tr>
                </thead>
                <tbody>
                  {(auditLogs.data?.rows || []).slice(0, 15).map((log: any) => (
                    <tr key={log.id}>
                      <td className="mono">{new Date(log.createdAt).toLocaleString()}</td>
                      <td>{log.action}</td>
                      <td>{log.actorUser?.email || 'System'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <h3>Sign-in security</h3>
            <p className="card-sub">Failed sign-ins and other security events we keep an eye on for you.</p>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Event</th>
                    <th>Severity</th>
                  </tr>
                </thead>
                <tbody>
                  {(securityEvents.data?.rows || []).slice(0, 15).map((event: any) => (
                    <tr key={event.id}>
                      <td className="mono">{new Date(event.createdAt).toLocaleString()}</td>
                      <td>{event.eventType}</td>
                      <td>
                        <span
                          className={`badge ${
                            event.severity === 'CRITICAL' || event.severity === 'HIGH'
                              ? 'danger'
                              : event.severity === 'MEDIUM'
                                ? 'warning'
                                : 'neutral'
                          }`}
                        >
                          {event.severity}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
